/**
 * TypeScript definitions generated from Group RFP 5.0.0 OpenAPI specification
 *
 * This file contains TypeScript interfaces, types, and enums that correspond
 * to the OpenAPI YAML schema definitions for the Group Insurance RFP and Quoting API.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  RfpComponents,
  RfpLtdOtherLimitationsEnum,
  StateTerritoryCodeEnum,
} from "@platform/openapi-types";
import { CoverageTypeEnum } from "@platform/types";

import type { FieldCommentResponse } from "../api/fieldCommentTypes";
import type { GridCommission } from "../api/commissionHelpers";

export type GroupRfp = RfpComponents["schemas"]["GroupRfp"];
export { BaseBenefitPlanPolicyPeriod, StateTerritoryCodeEnum };

// Dental Benefit Types
export type RfpDentalBenefit = RfpComponents["schemas"]["RfpDentalBenefit"];
export type RfpDentalNetworkBenefit =
  RfpComponents["schemas"]["RfpDentalNetworkBenefit"];
export type ServiceWaitingPeriod =
  RfpComponents["schemas"]["ServiceWaitingPeriod"];

// ===========================
// ENUMS
// ===========================

export enum CountryCodeEnum {
  US = "US",
  CA = "CA",
  MX = "MX",
}

export enum DependentRelationshipEnum {
  EMPLOYEE = "employee",
  SPOUSE = "spouse",
  CHILD = "child",
  DEPENDENT = "dependent",
  DOMESTIC_PARTNER = "domesticPartner",
  LEGAL_DEPENDENT = "legalDependent",
}

export enum EmploymentTypeEnum {
  FULL_TIME = "fullTime",
  PART_TIME = "partTime",
}

export { CoverageTypeEnum };

export enum GenderCodeEnum {
  MALE = "male",
  FEMALE = "female",
  NON_BINARY = "nonBinary",
}

export enum EmploymentStatusEnum {
  ACTIVE = "active",
  RETIRED = "retired",
  COBRA = "cobra",
  ON_LEAVE = "onLeave",
  TERMINATED = "terminated",
}

export enum CoverageTierEnum {
  EMPLOYEE = "employee",
  EMPLOYEE_DEPENDENT = "employeeDependent",
  EMPLOYEE_SPOUSE = "employeeSpouse",
  EMPLOYEE_CHILDREN = "employeeChildren",
  EMPLOYEE_FAMILY = "employeeFamily",
  EMPLOYEE_2_DEPENDENTS = "employee2Dependents",
  SPOUSE_ONLY = "spouseOnly",
  SPOUSE_DEPENDENT = "spouseDependent",
  SPOUSE_CHILDREN = "spouseChildren",
  CHILD_ONLY = "childOnly",
  WAIVED = "waived",
}

export enum BenefitPlanTypeEnum {
  PPO = "ppo",
  HMO = "hmo",
  EPO = "epo",
  POS = "pos",
  INDEMNITY = "indemnity",
  NOT_APPLICABLE = "notApplicable",
}

export enum FundingTypeEnum {
  FULLY_INSURED = "fullyInsured",
  SELF_FUNDED_ASO = "selfFundedASO",
  SELF_FUNDED_ATP = "selfFundedATP",
  CHECK_CUTTING = "checkCutting",
  NOT_APPLICABLE = "notApplicable",
}

export enum PlanOptionNameEnum {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
  NOT_APPLICABLE = "notApplicable",
}

export enum RateDesignEnum {
  COMPOSITE = "composite",
  GENDER = "gender",
  FLAT_RATE = "flatRate",
  AGE_BANDED = "ageBanded",
  AGE_BANDED_GENDER = "ageBandedGender",
  AGE_BANDED_TOBACCO = "ageBandedTobacco",
  AGE_BANDED_TIERED = "ageBandedTiered",
}

export enum RateUnitEnum {
  USD = "usd",
  VOLUME_PER_10 = "volumePer10",
  VOLUME_PER_100 = "volumePer100",
  VOLUME_PER_1000 = "volumePer1000",
  PER_10_OF_SALARY = "per10OfSalary",
  PER_100_OF_SALARY = "per100OfSalary",
  PER_1000_OF_SALARY = "per1000OfSalary",
}

export enum NetworkEnum {
  IN_NETWORK = "inNetwork",
  OUT_OF_NETWORK = "outOfNetwork",
}

export enum EmployerContributionEnum {
  CONTRIBUTORY = "contributory",
  NON_CONTRIBUTORY = "nonContributory",
  VOLUNTARY = "voluntary",
}

export enum CurrentCommissionTypeEnum {
  HEAPED = "heaped",
  PERCENTAGE = "percentage",
  STANDARD = "standard",
  GRADED = "graded",
  NET = "net",
  PEPM = "pepm",
  NOT_APPLICABLE = "notApplicable",
}

export enum CommissionTypeEnum {
  HEAPED = "heaped",
  PERCENTAGE = "percentage",
  STANDARD = "standard",
  GRADED = "graded",
  NET = "net",
  PEPM = "pepm",
}

export enum RfpInclusionStatusEnum {
  INCLUDED = "included",
  NOT_INCLUDED = "notIncluded",
}

export enum RfpAnnualOpenEnrollmentEnum {
  ANNUAL_OPEN_ENROLLMENT = "annualOpenEnrollment",
  NOT_OFFERED = "notOffered",
}

export enum RfpDeductibleDurationEnum {
  CALENDAR_YEAR = "calendarYear",
  PLAN_YEAR = "planYear",
  LIFETIME = "lifetime",
  NOT_INCLUDED = "notIncluded",
}

export enum OutOfNetworkReimbursementEnum {
  CUSTOMARY_REASONABLE = "customaryReasonable",
  MAC = "mac",
  NINETIETH_UCR = "90thUcr",
  EIGHTIETH_UCR = "80thUcr",
  SEVENTIETH_UCR = "70thUcr",
}

export enum RfpDollarBenefitEnum {
  DOLLAR_AMOUNT = "dollarAmount",
  NOT_INCLUDED = "notIncluded",
}

export enum RfpPercentageBenefitTypeEnum {
  PERCENTAGE_AMOUNT = "percentageAmount",
  NOT_INCLUDED = "notIncluded",
}

export enum RfpDentalServiceCategoryEnum {
  PREVENTIVE = "preventive",
  BASIC = "basic",
  MAJOR = "major",
  ORTHODONTIC = "orthodontic",
  NOT_COVERED = "notCovered",
}

// ===========================
// BASE INTERFACES
// ===========================

export interface Address {
  firstLine: string;
  secondLine?: string;
  thirdLine?: string;
  cityName: string;
  stateProvinceCode: StateTerritoryCodeEnum;
  postalCode: string;
  countryCode?: CountryCodeEnum;
}

export interface ContactName {
  firstName: string;
  lastName: string;
}

export interface ContactSlim extends ContactName {
  email: string;
}

export interface File {
  identifier?: string;
  uri?: string;
}

export interface BenefitClass {
  id: string;
  name: string;
  identifier: string;
}

// ===========================
// EMPLOYER & ORGANIZATION
// ===========================

export interface Employer {
  name: string;
  numberOfEligibleEmployees: number;
  hasEmployeeOutsideUS: boolean;
  address: Address;
  situsState: StateTerritoryCodeEnum;
}

export interface Broker {
  name: string;
  address: Address;
  accountManager: ContactName;
  producer: ContactSlim;
  additionalAgent?: ContactSlim;
  commissions: RfpCommissions[];
}

export interface GeneralAgent {
  name: "Centro Benefits Research, LLC";
  producer: ContactName;
  centroRfpAnalyst: ContactSlim;
}

// ===========================
// MEMBER & COVERAGE
// ===========================

export interface MemberCoverage {
  benefitClassIdentifier?: string;
  coverageType: CoverageTypeEnum;
  isWaived?: boolean;
  coverageTierCode?: CoverageTierEnum;
  benefitPlanName?: string;
  coverageAmountType?: "flatAmount" | "salaryMultiplier" | "percentage";
  coverageAmount?: number;
}

export interface GroupMember {
  employeeCode: string;
  dependentRelationship: DependentRelationshipEnum;
  employmentType?: EmploymentTypeEnum;
  gender?: GenderCodeEnum;
  birthDate?: string; // ISO-8601 date format
  postalCode?: string;
  employmentStatus?: EmploymentStatusEnum;
  employeeJobTitle?: string;
  annualIncome?: number;
  coverage?: MemberCoverage[];
}

export type GroupCensus = GroupMember[];

// ===========================
// RATES & FINANCIALS
// ===========================

export interface Rate {
  ageBandLower?: number;
  ageBandUpper?: number;
  coverageTierCode?: CoverageTierEnum;
  numberOfLives?: number;
  gender?: "male" | "female";
  isTobaccoRated?: boolean;
  rate?: number;
  volume?: number;
  unit: RateUnitEnum;
}

export interface RateSchedule {
  rateDesign: RateDesignEnum;
  numberOfLives?: number;
  pepm?: number;
  rates?: Rate[];
  rateCoverageIndicator?: "life" | "adnd";
}

export interface RfpCommissions {
  coverageType: CoverageTypeEnum;
  benefitPlanIdentifier: string;
  currentType: CurrentCommissionTypeEnum;
  currentAmount?: number;
  currentHeapedAmount?: number[];
  requestedType: CommissionTypeEnum;
  requestedAmount?: number;
  requestedHeapedAmount?: number[];
}

export interface CommissionsBase {
  coverageType?: CoverageTypeEnum;
  benefitPlanIdentifier?: string;
}

export interface TechCredit {
  benAdminSystem: string;
  amountType: "percentage" | "dollarAmount" | "pepm" | "carrierStandard";
  amount: number;
}

export interface AdditionalCoverages {
  types?: ("pet" | "hyattLegal" | "smd" | "gvul" | "gul" | "other")[];
  notes?: string;
}

// ===========================
// BENEFIT STRUCTURES
// ===========================

export interface RfpDollarBenefit {
  type: RfpDollarBenefitEnum;
  amount?: number;
}

export interface RfpPercentageBenefit {
  type: RfpPercentageBenefitTypeEnum;
  amount?: number; // 0-100
}

export interface BaseOrthoAgeLimit {
  type: "childrenOnlyToAge" | "notIncluded";
  age?: number;
}

export interface RfpOrthoAgeLimit extends BaseOrthoAgeLimit {
  type: "childrenOnlyToAge" | "notIncluded";
}

// ===========================
// DENTAL BENEFITS
// ===========================

export interface BaseDentalNetworkBenefit {
  network: NetworkEnum;
  individualDeductible?: RfpDollarBenefit;
  familyDeductible?: RfpDollarBenefit;
  annualMaximum?: RfpDollarBenefit;
  preventativeType1?: RfpPercentageBenefit;
  basicType2?: RfpPercentageBenefit;
  majorType3?: RfpPercentageBenefit;
  orthoType4?: RfpPercentageBenefit;
  orthoMaximum?: RfpDollarBenefit;
  endodontics?: RfpDentalServiceCategoryEnum;
  periodonticsNonSurgical?: RfpDentalServiceCategoryEnum;
  periodonticsSurgical?: RfpDentalServiceCategoryEnum;
  oralSurgerySimple?: RfpDentalServiceCategoryEnum;
  oralSurgeryComplex?: RfpDentalServiceCategoryEnum;
  implants?: RfpDentalServiceCategoryEnum;
  sealants?: RfpDentalServiceCategoryEnum;
  spaceMaintainers?: RfpDentalServiceCategoryEnum;
  generalAnesthesia?: RfpDentalServiceCategoryEnum;
  compositeWhiteFillings?: RfpDentalServiceCategoryEnum;
  orthoAgeLimit?: RfpOrthoAgeLimit;
}

// ===========================
// VISION BENEFITS
// ===========================

export interface RfpVisionCopayRule {
  type: "dollarAmount" | "notIncluded";
  amount?: number;
}

export interface RfpVisionDiscountRule {
  type: "percentageDiscount" | "notIncluded";
  amount?: number;
}

export interface RfpVisionCopayAllowanceRule {
  type:
    | "coveredInFullAfterCopay"
    | "coveredInFull"
    | "dollarAmountAllowance"
    | "dollarAmountAllowanceWithCopay"
    | "notIncluded";
  copayAmount?: number;
  allowanceAmount?: number;
}

export interface BaseVisionNetworkBenefit {
  networkName?: string;
}

export interface RfpVisionNetworkBenefit extends BaseVisionNetworkBenefit {
  network: NetworkEnum;
  examCopay?: RfpVisionCopayRule;
  materialsCopay?: RfpVisionCopayRule;
  singleLenses?: RfpVisionCopayAllowanceRule;
  bifocalLenses?: RfpVisionCopayAllowanceRule;
  trifocalLenses?: RfpVisionCopayAllowanceRule;
  lenticularLenses?: RfpVisionCopayAllowanceRule;
  standardProgressive?: RfpVisionCopayAllowanceRule;
  standardFrames?: RfpVisionCopayAllowanceRule;
  additionalDiscountsOnFrames?: RfpVisionDiscountRule;
  contactLensElective?: RfpVisionCopayAllowanceRule;
  contactLensExam?: RfpVisionCopayAllowanceRule;
}

export interface RfpVisionBenefit {
  objectType?: "vision";
  networkBenefits?: RfpVisionNetworkBenefit[];
  examFrequency?:
    | "twelveTwelveTwentyFour"
    | "twelveTwelveTwelve"
    | "calendarYearCalendarYearEveryOther"
    | "calendarYearCalendarYearCalendarYear"
    | "notIncluded";
  lensesFrequency?:
    | "twelveTwelveTwentyFour"
    | "twelveTwelveTwelve"
    | "calendarYearCalendarYearEveryOther"
    | "calendarYearCalendarYearCalendarYear"
    | "notIncluded";
  framesFrequency?:
    | "twelveTwelveTwentyFour"
    | "twelveTwelveTwelve"
    | "calendarYearCalendarYearEveryOther"
    | "calendarYearCalendarYearCalendarYear"
    | "notIncluded";
  employerContribution?: EmployerContributionEnum;
  annualOpenEnrollment?: RfpAnnualOpenEnrollmentEnum;
}

// ===========================
// LIFE & DISABILITY BENEFITS
// ===========================

export interface RfpBasicLifeAdndBenefit {
  objectType?: "basicLifeAdnd";

  // Benefit Amount - flattened from nested object
  benefitAmountType?:
    | "increments"
    | "dollarAmount"
    | "salaryMultiplier"
    | "salaryMultiplierPlusDollarAmount"
    | "allGuaranteeIssue";
  benefitAmountDollarAmount?: number;
  benefitAmountSalaryMultiplier?: number;

  // Benefit Maximum - flattened from nested object
  benefitMaximumType?: "dollarAmount" | "salaryMultiplier";
  benefitMaximumAmount?: number;

  // Guarantee Issue - flattened from nested object
  guaranteeIssueType?:
    | "dollarAmount"
    | "salaryMultiplierWithCap"
    | "salaryMultiplierWithDollarAmountPlusAdditionalAmount"
    | "allGuaranteeIssue"
    | "notIncluded";
  guaranteeIssueSalaryMultiplier?: number;
  guaranteeIssueDollarAmount?: number;
  guaranteeIssueAdditionalAmount?: number;

  // Definition of Earnings - flattened from nested object
  definitionOfEarningsType?:
    | "salary"
    | "salaryPlusCommissions"
    | "salaryPlusBonus"
    | "salaryPlusBonusPlusCommissions"
    | "priorYearW2"
    | "k1"
    | "notApplicable"
    | "notIncluded";
  definitionOfEarningsEarningsAverageMonths?: number;

  // Portability
  portability?:
    | "included"
    | "notIncluded"
    | "includedWithEOI"
    | "includedWithoutEOI";

  // Employer Contribution
  employerContribution?:
    | "oneHundredPercentEmployerPaid"
    | "contributory"
    | "voluntary"
    | "bonusUp"
    | "grossUp"
    | "taxChoice"
    | "notIncluded";

  // Spouse Benefit - flattened from nested object
  spouseBenefitType?: "dollarAmount" | "notIncluded";
  spouseBenefitAmount?: number;

  // Child Benefit - flattened from nested object
  childBenefitType?: "dollarAmount" | "notIncluded";
  childBenefitAmount?: number;

  // Age Reduction Schedule - flattened from nested object
  ageReductionScheduleType?: "percentageAtAge" | "notIncluded";
  ageReductionScheduleSchedules?: {
    ageBandLower?: number;
    percent?: number;
  }[];

  // Additional fields
  conversion?: "included" | "notIncluded";
  acceleratedDeathBenefit?: "included" | "notIncluded";
  waiverOfPremium?: "included" | "notIncluded";
}

export interface RfpVoluntaryLifeAdndBenefit {
  policyPeriod?: BaseBenefitPlanPolicyPeriod;
  currentCarrier?: string;
  benefitClassName?: string;
  // Employee Benefits
  benefitDescriptionEmployeeType?:
    | "increments"
    | "dollarAmount"
    | "multipleOptions"
    | "salaryMultiplier";
  benefitDescriptionEmployeeAmount?: number;
  benefitMaximumEmployeeType?:
    | "dollarAmount"
    | "salaryMultiplier"
    | "lesserOfDollarAmountOrSalaryMultiplier";
  benefitMaximumEmployeeDollarAmount?: number;
  benefitMaximumEmployeeSalaryMultiplier?: number;
  guaranteeIssueEmployeeType?:
    | "dollarAmount"
    | "ageBasedAmounts"
    | "allGuaranteeIssue"
    | "notIncluded";
  guaranteeIssueEmployeeDollarAmount?: number;
  guaranteeIssueAgesXAgeBandLower?: number;
  guaranteeIssueAgesXAgeBandUpper?: number;
  guaranteeIssueAgesXAmount?: number;
  // Spouse Benefits
  spouseBenefitsIncluded?: "yes" | "no";
  benefitDescriptionSpouseType?:
    | "dollarAmount"
    | "increments"
    | "multipleOptions"
    | "percentageOfEmployeeElection"
    | "notIncluded";
  benefitDescriptionSpouseAmount?: number;
  benefitMaximumSpouseType?: "dollarAmount" | "notIncluded";
  benefitMaximumSpouseAmount?: number;
  guaranteeIssueSpouseType?:
    | "dollarAmount"
    | "ageBasedAmounts"
    | "allGuaranteeIssue"
    | "notIncluded";
  guaranteeIssueSpouseDollarAmount?: number;
  guaranteeIssueSpouseAgesXAgeBandLower?: number;
  guaranteeIssueSpouseAgesXAgeBandUpper?: number;
  guaranteeIssueSpouseAgesXAmount?: number;
  amountNotToExceedSpouseType?:
    | "percentageOfEmployeeElection"
    | "notApplicable"
    | "notIncluded";
  amountNotToExceedSpouseAmount?: number;
  // Child Benefits
  childBenefitsIncluded?: "yes" | "no";
  benefitDescriptionChildDependentAgesBirthTo14DaysType?:
    | "dollarAmount"
    | "percentageOfEmployeeElection"
    | "multipleOptions"
    | "increments"
    | "notIncluded";
  benefitDescriptionChildDependentAgesBirthTo14DaysAmount?: number;
  benefitDescriptionChildDependentAges15DaysTo6MonthsType?:
    | "dollarAmount"
    | "percentageOfEmployeeElection"
    | "multipleOptions"
    | "increments"
    | "notIncluded";
  benefitDescriptionChildDependentAges15DaysTo6MonthsAmount?: number;
  benefitDescriptionChildDependentAges6MonthsToLimitingAgeType?:
    | "dollarAmount"
    | "percentageOfEmployeeElection"
    | "multipleOptions"
    | "increments"
    | "notIncluded";
  benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount?: number;
  benefitMaximumChildType?: "dollarAmount" | "notIncluded";
  benefitMaximumChildAmount?: number;
  amountNotToExceedChildType?:
    | "percentageOfEmployeeElection"
    | "notApplicable"
    | "notIncluded";
  amountNotToExceedChildAmount?: number;
  studentStatusStudentLimitingAgeType?: "included" | "notIncluded";
  studentLimitingAgeAmount?: number;
  // Employee Remaining Benefits
  definitionOfEarningsType?:
    | "salary"
    | "salaryPlusCommissions"
    | "salaryPlusBonus"
    | "salaryPlusBonusPlusCommissions"
    | "priorYearW2"
    | "k1"
    | "notApplicable"
    | "notIncluded";
  definitionOfEarningsAverageMonth?: number;
  adndIncluded?: "included" | "notIncluded";
  adndTiedToLifeElection?:
    | "yes"
    | "no"
    | "tied"
    | "untied"
    | "clientLevelDecision";
  ageReductionSchedule?: "percentageAtAge" | "none" | "notIncluded";
  ageReductionXAgeBandLower?: number;
  ageReductionXAgeBandUpper?: number;
  ageReductionXPercent?: number;
  conversion?: "included" | "notIncluded";
  acceleratedDeathBenefit?: "included" | "notIncluded";
  waiverOfPremium?: "included" | "notIncluded";
  annualOpenEnrollment?:
    | "oneTimeOpenEnrollment"
    | "annualOpenEnrollment"
    | "modifiedOpenEnrollment"
    | "included"
    | "notIncluded";
  portability?:
    | "included"
    | "notIncluded"
    | "includedWithEOI"
    | "includedWithoutEOI";
}

export interface StdBenefitAmount {
  amountType: "increments" | "percentageOfSalary";
  amount: number;
  minimum?: number;
}

export interface DisabilityMaximumAmount {
  type:
    | "dollarAmount"
    | "dollarAmountNotToExceedPercentageOfPredisabilityEarnings"
    | "unlimited";
  dollarAmount?: number;
  percent?: number;
}

export interface DefinitionOfEarnings {
  type: "salary" | "notIncluded";
  averageMonth?: number;
}

export interface RfpEliminationPeriodType {
  type: "days" | "notIncluded";
  amount?: number;
}

export interface RfpBenefitDurationType {
  type: "days" | "weeks" | "notIncluded";
  amount?: number;
}

export interface RfpPreExistingConditionExtended {
  type: "months" | "waived" | "notIncluded";
  monthsDiagnosisFreeBeforeEffDate?: number;
  monthsTreatmentFree?: number;
  monthsDiagnosisFreeAfterEffDate?: number;
}

export interface BaseStdBenefit {
  benefitAmount?: StdBenefitAmount;
  maximumAmount?: DisabilityMaximumAmount;
  definitionOfDisability?:
    | "lossOfDuties"
    | "lossOfEarnings"
    | "lossOfEarningsAndDuties"
    | "lossOfEarningsOrLossOfDuties";
  definitionOfEarnings?: DefinitionOfEarnings;
  employerContribution?: EmployerContributionEnum;
}

export interface RfpStdBenefit extends BaseStdBenefit {
  eliminationPeriod?: RfpEliminationPeriodType;
  benefitDuration?: RfpBenefitDurationType;
  preExistingCondition?: RfpPreExistingConditionExtended;
  ficaMatch?: "included" | "notIncluded";
  ficaMatchBillable?:
    | "billable"
    | "nonBillable"
    | "notApplicable"
    | "notIncluded";
  annualOpenEnrollment?: RfpAnnualOpenEnrollmentEnum;
}

export interface LtdBenefitAmount {
  type: string;
  amount: number;
  minimum?: number;
}

export interface BaseLtdBenefit {
  benefitAmount?: LtdBenefitAmount;
  maximumAmount?: DisabilityMaximumAmount;
  definitionOfDisability?:
    | "lossOfDuties"
    | "lossOfEarnings"
    | "lossOfEarningsAndDuties"
    | "lossOfEarningsOrLossOfDuties";
  definitionOfEarnings?: DefinitionOfEarnings;
  employerContribution?: EmployerContributionEnum;
}

export interface RfpLtdBenefit extends BaseLtdBenefit {
  guaranteedIssue?: {
    type: string;
    amount?: number;
  };
  ownOccupationPeriod?: {
    type: string;
    amount?: number;
  };
  gainfulEarningsTest?: {
    type: string;
    amount?: number;
  };
  eliminationPeriod?: {
    type: string;
    amount?: number;
  };
  benefitDuration?: {
    type: string;
    amount?: number;
  };
  mentalNervousLimitation?: {
    type: string;
    timeframe?: number;
  };
  substanceAbuseLimitation?: {
    type: string;
    timeframe?: number;
  };
  preExistingCondition?: {
    type: string;
    monthsDiagnosisFreeBeforeEffDate?: number;
    monthsTreatmentFree?: number;
    monthsDiagnosisFreeAfterEffDate?: number;
  };
  rehab?:
    | "mandatory"
    | "mandatoryWithEnhanced"
    | "voluntary"
    | "voluntaryWithEnhanced"
    | "enhanced"
    | "notIncluded";
  annualOpenEnrollment?: RfpAnnualOpenEnrollmentEnum;
  otherLimitations?: RfpLtdOtherLimitationsEnum;
}

// ===========================
// BENEFIT PLANS
// ===========================

export interface BaseBenefitPlan {
  identifier: string;
  id?: string; // UUID for the benefit plan
  numberOfLives?: number;
  name?: string;
  type?: BenefitPlanTypeEnum;
  policyPeriod?: BaseBenefitPlanPolicyPeriod;
  currentCarrierName?: string;
  fundingType?: FundingTypeEnum;
  participationRequirements?: string;
  rateGuarantee?: {
    type: "months" | "years" | "notIncluded" | "notOffered";
    amount?: number;
  };
  rateCap?: string;
  optionName?: PlanOptionNameEnum;
  benefitClassAvailability?: string[];
  eligibilityClasses?: BenefitClass[]; // Actively assigned eligibility classes for this plan
  rateSchedule?: RateSchedule[];
  fieldComments?: FieldCommentResponse[];
  isBenchmarkSeeded?: boolean;
  /**
   * True for quote-side plans synthesized at read time (placeholder/pad slots,
   * ADR-024 / CD-1612). Never set on request-side RFP plans. The row builder
   * skips benefit-definition defaults for these so they render as no-data.
   */
  isPlaceholder?: boolean;
}

export interface RfpBenefitPlan extends BaseBenefitPlan {
  benefits?: (
    | RfpVisionBenefit
    | RfpStdBenefit
    | RfpLtdBenefit
    | RfpBasicLifeAdndBenefit
    | RfpVoluntaryLifeAdndBenefit
  ) &
    Partial<GridCommission>;
}

// ===========================
// COVERAGE & RFP STRUCTURE
// ===========================

export interface BaseCoverage {
  type: CoverageTypeEnum;
  notes?: string;
}

/**
 * @deprecated This interface combines API fields and frontend-added fields. Use `RfpCoverage` from `./types` instead.
 */
export interface RfpCoverage extends BaseCoverage {
  id?: string; // UUID for the coverage
  status?: string;
  benefitPlans: RfpBenefitPlan[];
  columnOrder?: string[]; // Array of benefit plan UUIDs in display order
  benefitClasses?: BenefitClass[]; // Available benefit classes for this coverage
  eligibilityClasses?: BenefitClass[]; // Actively assigned eligibility classes for this coverage
}

export interface BaseGroupRfp {
  identifier?: string;
  versionNumber?: number;
  effectiveDate?: string; // ISO-8601 date
  dueDate?: string; // ISO-8601 date
  notes?: string;
  employer?: Employer;
  benefitClasses?: BenefitClass[];
  census?: GroupCensus;
  files?: File[];
  broker?: Broker;
  generalAgent?: GeneralAgent;
  techCredit?: TechCredit;
  additionalCoverages?: AdditionalCoverages;
}

export interface PostRfpCoverage extends BaseCoverage {
  status?: "quoteNeeded" | "quoteNotNeeded";
  benefitPlans?: RfpBenefitPlan[];
}

export interface PutRfpCoverage extends BaseCoverage {
  status?: "quoteNeeded" | "quoteNotNeeded";
  benefitPlans?: RfpBenefitPlan[];
  [key: string]: unknown; // Allow additional properties for flexibility
}

export interface PostGroupRfp extends BaseGroupRfp {
  coverages?: PostRfpCoverage[];
}

export interface GroupRfpData extends BaseGroupRfp {
  carrierIssuedIdentifier?: string;
  coverages?: RfpCoverage[]; // The newer Coverage type
  [key: string]: unknown; // Allow additional properties for flexibility
}

// ===========================
// PROPOSAL STRUCTURE
// ===========================

export interface GroupProposal {
  carrierIssuedIdentifier: string;
  rfpVersionNumber: number;
  proposalIdentifier: string;
  coverages?: RfpCoverage[];
  files?: File[];
  notes?: string;
}

// ===========================
// ERROR HANDLING
// ===========================

export interface FieldError {
  field: string;
  message: string;
  code?: string;
}

export interface ValidationError {
  message: string;
  errors: FieldError[];
}

export interface Error {
  message: string;
  code?: string;
  details?: string;
}

// ===========================
// API RESPONSE TYPES
// ===========================

export interface ApiResponse<T> {
  data?: T;
  error?: Error;
  validationErrors?: ValidationError;
}

export type CreateRfpResponse = ApiResponse<GroupRfp>;
export type UpdateRfpResponse = ApiResponse<void>;
export type GetProposalResponse = ApiResponse<GroupProposal>;
